import React, { FC, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FormInputText } from '../../../inputs/FormInputText';
import { FormInputSwitch } from '../../../inputs/FormInputSwitch';
import { ButtonClassNames, LocalStorageItems, GlobalRouteNames, InputRegexPattern } from '../../../../models/Common';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { TextButton, TextButtonClassNames } from '../../buttons/TextButton';
import { useAppSelector, useAppDispatch } from '../../../../hooks/redux';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { useDebounce } from '../../../../hooks/useDebounce';
import { IMultiAccount } from '../../../../models/UserInfo';
import { ApiError } from '../../../../models/ApiError';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import Avatar from '../../../../assets/images/user/Avatar.png';
import AvatarDark from '../../../../assets/images/user/AvatarDark.png';
import { getErrorMessage } from '../../../../utils';

interface SubAccountSettingsProps {
  userId: number;
  handleClose: () => void;
}

export const SubAccountSettings: FC<SubAccountSettingsProps> = ({ userId, handleClose }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { theme } = useAppSelector((state) => state.appReducer);
  const { data, isFetching } = userInfoApi.useGetMultiaccountMembersQuery(selectedLanguage, {
    refetchOnMountOrArgChange: true,
  });
  const [changeMember] = userInfoApi.useChangeMemberAccountMutation();
  const [pendingAction, setPendingAction] = useState<'autopay' | 'alias' | 'clear' | null>(null);
  const accountInfo = data?.List?.find((acc) => acc.UserID === userId);

  const methods = useForm<{ isAutopay: boolean; userAlias: string }>({
    mode: 'onChange',
    defaultValues: {
      isAutopay: !!accountInfo?.UseAutoPay,
      userAlias: accountInfo?.Alias || '',
    },
  });

  const { handleSubmit, setError, setValue, watch, clearErrors } = methods;

  useEffect(() => {
    if (!isFetching) {
      setPendingAction(null);
    }
  }, [isFetching]);

  const handleSwitch = useDebounce(async (isChecked: boolean) => {
    if (accountInfo?.UseAutoPay !== isChecked) {
      try {
        setPendingAction('autopay');
        await changeMember({ UserID: userId, useAutoPay: isChecked }).unwrap();
        if (isChecked) {
          customEnqueueSnackbar(t('multiAccount.autopayWasEnabled', { UserID: userId }), 'success');
        } else {
          customEnqueueSnackbar(t('multiAccount.autopayWasDisabled', { UserID: userId }), 'warning');
        }
      } catch (err) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  }, 600);

  const handleRemoveAlias = async () => {
    try {
      setPendingAction('clear');
      await changeMember({ UserID: userId, alias: null }).unwrap();
      setValue('userAlias', '', { shouldValidate: false, shouldDirty: false });
      clearErrors('userAlias');
      customEnqueueSnackbar(t('accountSettings.changesApplied'), 'success');
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleSaveAlias = async ({ userAlias }: { userAlias: string }) => {
    if (!userAlias?.trim()) {
      setError('userAlias', {
        type: 'local',
        message: t('validation.thisFieldIsRequired'),
      });
      return;
    }
    try {
      setPendingAction('alias');
      await changeMember({ UserID: userId, alias: userAlias.trim() }).unwrap();
      customEnqueueSnackbar(t('accountSettings.changesApplied'), 'success');
    } catch (err: unknown) {
      const error = err as ApiError;
      setError('userAlias', {
        type: 'server',
        message: error?.data?.error?.message,
      });
    }
  };

  const handleViewServices = (item?: IMultiAccount) => {
    handleClose();
    localStorage.setItem(LocalStorageItems.ControlledAccount, JSON.stringify(item));
    dispatch(userInfoApi.util.invalidateTags(['Accounts']));
    history.push(GlobalRouteNames.HomePage);
  };

  return (
    <div className="modals modals_account-settings">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.accountSettings')}</article>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleSaveAlias)}>
          <div className="setting-row">
            <div className="setting-row-heading-profile">
              <div className="setting-name">{t('accountSettings.information')}</div>
              <div className="setting-profile">
                <div className="setting-profile-thumbnail">
                  <img src={theme === 'light' ? Avatar : AvatarDark} alt="avatar" />
                </div>
                <div className="setting-profile-agreement">
                  {t('multiAccount.account')} <span>{userId}</span>
                </div>
              </div>
            </div>
            <div className="setting-row-personal">
              <div className="setting-row-personal-item">
                <div className="setting-row-personal-title">{t('accountSettings.fullName')}</div>
                <div className="setting-row-personal-value">{accountInfo?.User?.Name}</div>
              </div>
              <div className="setting-row-personal-item">
                <div className="setting-row-personal-title">{t('userInfo.address')}</div>
                <div className="setting-row-personal-value">
                  <TextButton
                    label={`${accountInfo?.User?.Address}`}
                    handleClick={() => handleViewServices(accountInfo)}
                    className={TextButtonClassNames.PrimaryActive}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="setting-row">
            <div className="setting-row-heading">
              <div className="setting-name">{t('multiAccount.autopay')}</div>
              <div className="setting-autopay">
                <FormInputSwitch
                  handleChange={handleSwitch}
                  isDisabled={pendingAction === 'autopay'}
                  name="isAutopay"
                />
              </div>
            </div>
            <div className="setting-row-note">{t('multiAccount.autopayDisabledPaymentWarning')}</div>
          </div>
          <div className="setting-row">
            <div className="setting-row-heading">
              <div className="setting-name">{t('accountSettings.accountName')}</div>
            </div>
            <div className="setting-row-form">
              <div className="setting-row-form__input">
                <FormInputText
                  isRequired
                  name="userAlias"
                  className="primary"
                  pattern={InputRegexPattern.Any}
                  label={t('input.accountName')}
                  maxLength={50}
                />
              </div>
              <div className="setting-row-form__actions">
                <CustomMUIButton
                  label={t('buttons.delete')}
                  handleClick={handleRemoveAlias}
                  className={ButtonClassNames.SecondaryOutlined}
                  isBold
                  isDisabled={pendingAction === 'clear' || !accountInfo?.Alias}
                />
                <CustomMUIButton
                  label={t('buttons.save')}
                  type="submit"
                  className={ButtonClassNames.PrimaryFulfilled}
                  isBold
                  isDisabled={!watch('userAlias')?.trim()}
                  isLoading={pendingAction === 'alias'}
                />
              </div>
            </div>
            <div className="setting-row-note">{t('multiAccount.quickSearchName')}</div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
